import type { TypedDocumentNode } from '@graphql-typed-document-node/core';

import {
  COMPOSITION_GET_DATA_V2,
  COMPOSITION_HEADCOUNT_ONLY,
  type CompositionV2BreakdownOptions,
  type CompositionV2DataQuery,
  type CompositionV2DataQueryVariables,
  type CompositionV2Entity,
} from '@revelio/data-access';

import { useCustomSkillTaxonomyTree } from '../../../../../shared/selection-lists/hooks/useCustomSkillTaxonomyTree';
import { useSelectionListState } from '../../../../../shared/selection-lists/hooks/useSelectionListState';
import { useIsTaxonomyResolved } from '../../../filters/contexts/FilterProvider';
import { useEffectiveFilters } from '../../../filters/hooks/useEffectiveFilters';
import { useGeographyMode } from '../../../filters/hooks/useGeographyMode';
import { useRoleSelectionLists } from '../../../filters/selectionList/useRoleSelectionList';
import {
  FilterId,
  FilterType,
  type PrimaryFilterId,
  type TreeFilter,
} from '../../../filters/types/filters';
import { getCompositionV2Filters } from '../../../filters/utils/getCompositionV2Filters';
import {
  effectiveFiltersWithSinglePrimaryEntity,
  useChartPrimaryFilter,
} from '../../../hooks/useChartPrimaryFilter';
import { ChartLoader } from '../../Chart/ChartLoader';
import { useChart } from '../../Chart/ChartProvider';
import { useTabContentConfig } from '../../PageConfigProvider/PageConfigProvider';
import { RevelioPageId } from '../../PageConfigProvider/types';
import { isCompositionSecondaryDimension } from '../../PageConfigProvider/types/dimension';
import type { PositionedItem } from '../../PageConfigProvider/types/item';
import { usePageState } from '../../PageState/PageStateProvider';
import { haveSelectionsBeenModified } from '../../PageState/types';
import type { DataProviderChildren } from '../types';
import { useUrqlQueryWithStatus } from '../utils/data-provider-results-utils';

type CompositionDataProviderProps = {
  primaryDimension: PrimaryFilterId;
  isTimeseries?: boolean;
  entitiesOverride?: CompositionV2Entity[];
  getSelectionBreakdowns?: boolean;
  returnAllSkills?: boolean;
  children: DataProviderChildren<CompositionV2DataQuery | undefined>;
};

export const CompositionRawDataProvider = ({
  primaryDimension,
  isTimeseries = false,
  entitiesOverride,
  getSelectionBreakdowns,
  returnAllSkills,
  children,
}: CompositionDataProviderProps) => {
  if (getSelectionBreakdowns) {
    return (
      <CompositionBreakdownDataProvider
        primaryDimension={primaryDimension}
        isTimeseries={isTimeseries}
        entitiesOverride={entitiesOverride}
        // biome-ignore lint/correctness/noChildrenProp: migrated from develop
        children={children}
      />
    );
  }

  return (
    <CompositionNonBreakdownDataProvider
      primaryDimension={primaryDimension}
      isTimeseries={isTimeseries}
      entitiesOverride={entitiesOverride}
      allSkills={returnAllSkills}
      // biome-ignore lint/correctness/noChildrenProp: migrated from develop
      children={children}
    />
  );
};

const CompositionNonBreakdownDataProvider = ({
  primaryDimension,
  isTimeseries = false,
  entitiesOverride,
  allSkills = false,
  children,
}: CompositionDataProviderProps & { allSkills?: boolean }) => {
  const { revelioPageId } = useTabContentConfig();
  const effectiveFilters = useEffectiveFilters({ viewGranularity: 'month' });
  const geographyMode = useGeographyMode();
  const isAdministrative = geographyMode === 'administrative';

  // Company overview only needs headcount — use the lightweight query.
  // All other pages (including geo/role overviews) need the full query
  // for growth + breakdown data.
  const query =
    revelioPageId === RevelioPageId.COMPANY_OVERVIEW_V2
      ? COMPOSITION_HEADCOUNT_ONLY
      : COMPOSITION_GET_DATA_V2;

  const variables = getCompositionV2Filters({
    primaryDimension,
    effectiveFilters,
    isSnapshot: !isTimeseries,
    geographyMode,
  });

  const compositionVariablesWithEntitiesOverride = entitiesOverride
    ? { ...variables, entities: entitiesOverride }
    : variables;

  const baseBreakdownOptions: CompositionV2BreakdownOptions = {
    ...(allSkills && { Skill: { all: true } }),
    ...(isAdministrative && { StateOverMsa: true }),
  };

  const variablesWithSkills: CompositionV2DataQueryVariables =
    Object.keys(baseBreakdownOptions).length > 0
      ? {
          ...compositionVariablesWithEntitiesOverride,
          breakdownOptions: baseBreakdownOptions,
        }
      : compositionVariablesWithEntitiesOverride;

  if (
    !variablesWithSkills?.filters?.start_date ||
    !variablesWithSkills?.filters?.end_date
  ) {
    return <ChartLoader />;
  }

  return (
    <CompositionQuery query={query} variables={variablesWithSkills}>
      {children}
    </CompositionQuery>
  );
};

const CompositionBreakdownDataProvider = ({
  primaryDimension,
  isTimeseries = false,
  entitiesOverride,
  children,
}: CompositionDataProviderProps) => {
  const effectiveFilters = useEffectiveFilters({ viewGranularity: 'month' });
  const { selectionLists, isSelectionListsLoading } = useSelectionListState();

  const roleSelectionLists = useRoleSelectionLists();
  const { customSkillTree } = useCustomSkillTaxonomyTree();

  const chartPrimaryFilter = useChartPrimaryFilter({ primaryDimension });
  const { item } = useChart();

  const effectiveFiltersWithSingleEntity =
    effectiveFiltersWithSinglePrimaryEntity({
      item,
      effectiveFilters,
      chartPrimaryFilter,
    });

  const geographyMode = useGeographyMode();

  const { config, revelioPageId } = useTabContentConfig();
  const { pageState } = usePageState();
  const isModified = item?.id
    ? haveSelectionsBeenModified(pageState, item.id)
    : false;

  const configItems = config.items ?? [];

  const shareOfSegmentPlotsNeedingSubfilters = configItems.filter(
    (chartItem) => {
      if (!plotUsesShareOfSegmentPageSelections(chartItem)) return false;
      const key = canonicalizeBreakdownKey(chartItem.plot?.secondaryDimension);
      return (
        key !== null &&
        (SUPPORTED_BREAKDOWN_KEYS.has(key) || key === STATE_OVER_MSA_KEY)
      );
    }
  );

  const subFilterSelectionsByPlotId: SubFilterSelectionsByPlotId = (() => {
    const base: Record<string, SelectionListEntry[] | undefined> = {
      ...(isModified
        ? {
            ...pageState.initialSelections,
            [item.id]: pageState.selections?.[item?.id],
          }
        : pageState.initialSelections),
    };
    shareOfSegmentPlotsNeedingSubfilters.forEach((p) => {
      if (base[p.id] === undefined) {
        base[p.id] = [];
      }
    });
    return base;
  })();

  const allBreakdownSelectionsReady = areAllBreakdownSelectionsReady(
    configItems,
    subFilterSelectionsByPlotId
  );

  const isAdministrative = geographyMode === 'administrative';

  const breakdownOptions = deriveBreakdownOptionsFromSelections({
    configItems,
    currentPlotId: item.id,
    subFilterSelectionsByPlotId,
  });

  const breakdownOptionsWithGeoMode: CompositionV2BreakdownOptions = {
    ...breakdownOptions,
    ...(isAdministrative && { StateOverMsa: true }),
  };

  const revelioPageIdBreakdownOptions = (() => {
    if (revelioPageId) return breakdownOptionsWithGeoMode;
    if (Object.keys(breakdownOptionsWithGeoMode).length === 0) return undefined;
    return breakdownOptionsWithGeoMode;
  })();

  const variables = getCompositionV2Filters({
    breakdownOptions: revelioPageIdBreakdownOptions,
    primaryDimension,
    effectiveFilters: effectiveFiltersWithSingleEntity,
    getSelectionBreakdowns: true,
    isSnapshot: !isTimeseries,
    selectionLists: { ...selectionLists, ...roleSelectionLists },
    customSkillTree,
    customSkillSelections: (() => {
      const cs = effectiveFiltersWithSingleEntity.find(
        (f) => f.id === FilterId.CUSTOM_SKILL && f.type === FilterType.TREE
      ) as TreeFilter | undefined;
      return cs?.selections?.map((s) => s.id) ?? [];
    })(),
    customSkillDeepestLevel: (() => {
      const cs = effectiveFiltersWithSingleEntity.find(
        (f) => f.id === FilterId.CUSTOM_SKILL && f.type === FilterType.TREE
      ) as TreeFilter | undefined;
      if (!cs) return null;
      const order: Record<string, 0 | 1 | 2 | 3 | 4> = {
        skill_0: 0,
        skill_1: 1,
        skill_2: 2,
        skill_3: 3,
        skill_4: 4,
      };
      let deepest: 0 | 1 | 2 | 3 | 4 | null = null;
      cs.selections.forEach((s) => {
        const lv = order[s.levelId as string];
        if (lv === undefined) return;
        if (deepest === null || lv > deepest) deepest = lv;
      });
      if (deepest === null) return null;
      return (['skill_0', 'skill_1', 'skill_2', 'skill_3', 'skill_4'] as const)[
        deepest
      ];
    })(),
    geographyMode,
  });

  const compositionVariablesWithEntitiesOverride = entitiesOverride
    ? { ...variables, entities: entitiesOverride }
    : variables;

  const allListsLoaded = Boolean(
    !isSelectionListsLoading &&
      Object.keys(selectionLists ?? {}).length &&
      (customSkillTree?.length ?? 0) &&
      Object.keys(roleSelectionLists ?? {}).length
  );

  const entities = compositionVariablesWithEntitiesOverride.entities;
  const entitiesEmpty = Array.isArray(entities) && entities.length === 0;

  if (
    !allListsLoaded ||
    !allBreakdownSelectionsReady ||
    !compositionVariablesWithEntitiesOverride?.filters?.start_date ||
    !compositionVariablesWithEntitiesOverride?.filters?.end_date ||
    entitiesEmpty
  ) {
    return <ChartLoader />;
  }

  return (
    <CompositionQuery
      variables={compositionVariablesWithEntitiesOverride}
      haveSelectionsBeenModified={isModified}
    >
      {children}
    </CompositionQuery>
  );
};

const CompositionQuery = ({
  query = COMPOSITION_GET_DATA_V2,
  variables,
  children,
  haveSelectionsBeenModified,
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  // biome-ignore lint/suspicious/noExplicitAny: migrated from develop
  query?: TypedDocumentNode<any, any>;
  variables: CompositionV2DataQueryVariables;
  children: DataProviderChildren<CompositionV2DataQuery | undefined>;
  haveSelectionsBeenModified?: boolean;
}) => {
  const isTaxonomyResolved = useIsTaxonomyResolved();
  const [urqlState] = useUrqlQueryWithStatus<CompositionV2DataQuery>({
    query,
    variables,
    pause: !isTaxonomyResolved,
  });
  const { data, status, error } = urqlState;

  return children({
    data,
    status,
    error: error ?? undefined,
    haveSelectionsBeenModified,
  });
};

type SelectionListEntry = { id: string };

type SubFilterSelectionsByPlotId =
  | Record<string, SelectionListEntry[] | undefined>
  | undefined;

const SUPPORTED_BREAKDOWN_KEYS = new Set([
  'Geography',
  'Rics',
  'Role',
  'Skill',
]);
const SKILL_KEY = 'Skill';
const STATE_OVER_MSA_KEY = 'StateOverMsa';

const plotUsesShareOfSegmentPageSelections = (item: PositionedItem): boolean =>
  item.plot?.chartType === 'share-of-segment' &&
  item.plot.secondaryDimension !== undefined &&
  isCompositionSecondaryDimension(item.plot.secondaryDimension);

/**
 * Returns true when every Share-of-segment plot already has an entry in the
 * page-state selections map. Prevents the composition query from firing before
 * those charts dispatch their initial `SET_SELECTIONS`, which would otherwise
 * cause the query to re-fire as each chart hydrates.
 */
const areAllBreakdownSelectionsReady = (
  configItems: PositionedItem[],
  subFilterSelections: SubFilterSelectionsByPlotId
): boolean => {
  const plotsNeedingSelections = configItems.filter((item) => {
    if (!plotUsesShareOfSegmentPageSelections(item)) return false;
    const key = canonicalizeBreakdownKey(item.plot?.secondaryDimension);
    return (
      key !== null &&
      (SUPPORTED_BREAKDOWN_KEYS.has(key) || key === STATE_OVER_MSA_KEY)
    );
  });

  if (plotsNeedingSelections.length === 0) return true;

  return plotsNeedingSelections.every(
    (item) => subFilterSelections?.[item.id] !== undefined
  );
};

/**
 * Converts the page-state selections (grouped by plot/secondary dimension)
 * into the shape expected by `CompositionV2BreakdownOptions`.
 */
const deriveBreakdownOptionsFromSelections = ({
  configItems,
  currentPlotId,
  subFilterSelectionsByPlotId,
}: {
  configItems: PositionedItem[];
  currentPlotId?: string;
  subFilterSelectionsByPlotId: SubFilterSelectionsByPlotId;
}): CompositionV2BreakdownOptions => {
  const selectedBreakdownOptions = Object.entries(
    subFilterSelectionsByPlotId ?? {}
  ).reduce<CompositionV2BreakdownOptions>((options, [plotId, selections]) => {
    const canonicalKey = canonicalizeBreakdownKey(
      findEffectiveBreakdownDimension(configItems, plotId)
    );

    if (!canonicalKey || !SUPPORTED_BREAKDOWN_KEYS.has(canonicalKey)) {
      return options;
    }

    const item = findItem(configItems, plotId);
    const usesDimensionSourceFallback =
      item !== undefined &&
      item.plot?.secondaryDimension === undefined &&
      getDimensionSourceBreakdownDimension(item) !== undefined;

    return {
      ...options,
      [canonicalKey]: {
        ...(usesDimensionSourceFallback && selections === undefined
          ? { all: true }
          : {
              all: false,
              selected: mapSelectionIdsToNumbers(selections),
            }),
      },
    };
  }, {});

  const currentItem =
    currentPlotId === undefined
      ? undefined
      : findItem(configItems, currentPlotId);
  const currentDimensionSource =
    currentItem === undefined
      ? undefined
      : getDimensionSourceBreakdownDimension(currentItem);
  const currentCanonicalKey = canonicalizeBreakdownKey(currentDimensionSource);

  if (
    !currentCanonicalKey ||
    !SUPPORTED_BREAKDOWN_KEYS.has(currentCanonicalKey) ||
    Object.keys(selectedBreakdownOptions).includes(currentCanonicalKey)
  ) {
    return selectedBreakdownOptions;
  }

  return {
    ...selectedBreakdownOptions,
    [currentCanonicalKey]: { all: true },
  };
};

const findItem = (
  configItems: PositionedItem[],
  plotId: string
): PositionedItem | undefined => configItems.find((item) => item.id === plotId);

const findEffectiveBreakdownDimension = (
  configItems: PositionedItem[],
  plotId: string
): string | undefined => {
  const item = findItem(configItems, plotId);
  if (!item) {
    return undefined;
  }

  return (
    item.plot?.secondaryDimension ?? getDimensionSourceBreakdownDimension(item)
  );
};

const getDimensionSourceBreakdownDimension = (
  item: PositionedItem
): string | undefined => {
  const dimensionSource = item.plot?.dimensionSource;
  if (!dimensionSource || dimensionSource === 'primary') {
    return undefined;
  }

  if (!isCompositionSecondaryDimension(dimensionSource)) {
    return undefined;
  }

  const canonicalKey = canonicalizeBreakdownKey(dimensionSource);
  if (!canonicalKey || !SUPPORTED_BREAKDOWN_KEYS.has(canonicalKey)) {
    return undefined;
  }

  return dimensionSource;
};

const canonicalizeBreakdownKey = (key?: string): string | null => {
  if (!key) {
    return null;
  }

  const normalizedKey = key.toLowerCase();
  if (normalizedKey === 'skills' || normalizedKey === 'skill') {
    return SKILL_KEY;
  }

  return key.charAt(0).toUpperCase() + key.slice(1);
};

const mapSelectionIdsToNumbers = (
  selections?: SelectionListEntry[]
): number[] => {
  if (!selections) {
    return [];
  }

  return selections.map(({ id }) => {
    if (id.includes('_')) {
      const nestedSelectionListIds = id.split('_');
      return Number(nestedSelectionListIds[nestedSelectionListIds.length - 1]);
    }

    return Number(id);
  });
};
